import { AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@higgins/utils';
import { BarChart } from '@/components/charts/bar-chart';
import { LineChart } from '@/components/charts/line-chart';
import { KpiCard } from '@/components/charts/kpi-card';
import { MatrixTable } from '@/components/charts/matrix-table';
import { CardVisual } from '@/components/charts/card-visual';
import { VisualSkeleton } from '@/components/charts/visual-skeleton';
import type { SlotVisual } from '@/lib/api';
import type { SectionKey, ChartType, ChartRow, KpiData, MatrixData, CardItem } from '@/lib/types';

interface VisualSlotProps {
  section: SectionKey;
  slot: number;
  displayName?: string;
  visual?: SlotVisual;     // mapped data for this slot from the report's snapshot
  loading?: boolean;       // whole-report visuals are still loading
  className?: string;
  expectedChartType?: ChartType;
}

export function VisualSlot({ section, slot, displayName, visual, loading, className, expectedChartType }: VisualSlotProps) {
  void section; void slot;
  const title  = visual?.title ?? displayName;
  const failed = !loading && (!visual || !visual.ok);

  return (
    <div className={cn(
      'flex flex-col rounded-md border border-border bg-muted overflow-hidden',
      expectedChartType === 'kpi' ? 'h-44' : 'min-h-72',
      className,
    )}>
      {title && (
        <div className="flex-shrink-0 flex items-center justify-between px-2.5 py-1.5 bg-slate-700 text-white">
          <span className="text-xs font-medium truncate">{title}</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-white/60 flex-shrink-0 ml-1" />}
        </div>
      )}

      {expectedChartType === 'matrix' ? (
        /* Matrix: auto-height, no absolute positioning so the table sets its own size */
        <div className="relative p-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
            </div>
          )}
          {failed && <SlotError message={visual?.error} />}
          {visual?.ok && <ChartRenderer visual={visual} />}
        </div>
      ) : (
        <div className="relative flex-1 min-h-0">
          {loading && (
            <div className="absolute inset-0">
              <VisualSkeleton chartType={expectedChartType ?? 'bar'} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/80 rounded-full p-2.5 shadow-sm">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                </div>
              </div>
            </div>
          )}
          {failed && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <SlotError message={visual?.error} />
            </div>
          )}
          {visual?.ok && (
            <div className="absolute inset-0 p-2">
              <ChartRenderer visual={visual} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SlotError({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 text-muted-foreground p-4 text-center">
      <AlertCircle className="w-5 h-5 text-destructive" />
      <span className="text-xs">No data captured</span>
      {message && <span className="text-[10px] text-muted-foreground/70 max-w-[200px] truncate" title={message}>{message}</span>}
    </div>
  );
}

function ChartRenderer({ visual }: { visual: SlotVisual }) {
  const { chartType, data: chartData, meta } = visual;

  switch (chartType) {
    case 'bar':
      return (
        <BarChart
          data={chartData as ChartRow[]}
          index={meta?.index}
          keys={meta?.keys}
          unit={meta?.unit}
          stacked={meta?.stacked}
        />
      );
    case 'line':
      return (
        <LineChart
          data={chartData as ChartRow[]}
          index={meta?.index}
          keys={meta?.keys}
          unit={meta?.unit}
        />
      );
    case 'kpi':
      return <KpiCard data={chartData as KpiData} />;
    case 'matrix':
      return <MatrixTable data={chartData as MatrixData} unit={meta?.unit} />;
    case 'card':
      return <CardVisual data={chartData as CardItem[]} />;
    default:
      return null;
  }
}
